import { cn } from '@/lib/utils';
import * as React from 'react';

import { MarkdownContent } from '@/components/ui/markdown-content';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';

type MarkdownTextareaProps = Omit<
    React.ComponentProps<typeof Textarea>,
    'value'
> & {
    value: string;
    previewClassName?: string;
};

const TAB_TRIGGER_CLASSNAME =
    'relative px-3 py-1 border border-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-md font-medium text-muted-foreground text-xs data-[state=active]:text-foreground hover:text-foreground transition-all';

const MarkdownTextarea = React.forwardRef<
    HTMLTextAreaElement,
    MarkdownTextareaProps
>(({ className, previewClassName, value, id, ...props }, ref) => {
    const [mode, setMode] = React.useState<'write' | 'preview'>('write');

    return (
        <div className="border border-input rounded-md focus-within:border-ring/30 focus-within:ring-1 focus-within:ring-ring/30 overflow-hidden">
            <Tabs
                value={mode}
                onValueChange={(next) => setMode(next as 'write' | 'preview')}
            >
                <TabsList
                    indicatorClassName="bg-background shadow-xs border border-border rounded-md"
                    className="justify-start gap-1 bg-muted/40 p-1.5 border-border border-b rounded-none w-full h-auto">
                    <TabsTrigger
                        value="write"
                        className={TAB_TRIGGER_CLASSNAME}
                    >
                        Write
                    </TabsTrigger>
                    <TabsTrigger
                        value="preview"
                        className={TAB_TRIGGER_CLASSNAME}
                    >
                        Preview
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            {mode === 'write' ? (
                <Textarea
                    ref={ref}
                    id={id}
                    value={value}
                    className={cn(
                        'bg-background border-0 focus-visible:border-0 rounded-none focus-visible:ring-0',
                        className
                    )}
                    {...props}
                />
            ) : (
                <div
                    className={cn(
                        'bg-background px-3 py-2 overflow-y-auto',
                        previewClassName ?? className
                    )}
                >
                    {value.trim() ? (
                        <MarkdownContent>{value}</MarkdownContent>
                    ) : (
                        <p className="text-muted-foreground text-sm">
                            Nothing to preview yet
                        </p>
                    )}
                </div>
            )}
        </div>
    );
});
MarkdownTextarea.displayName = 'MarkdownTextarea';

export { MarkdownTextarea };
